import React, { useState } from 'react';
import { ExternalLink, Code2, ArrowRight, Zap, Database, Brain, Shield, Layers, DollarSign } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Projects = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Group projects by category for better navigation
  const categories = [
    { id: 'all', label: 'All Projects', icon: Layers },
    { id: 'ai', label: 'AI Systems', icon: Brain },
    { id: 'domain', label: 'Domain Specific Systems', icon: DollarSign }
  ];

  const featuredProjects = data.filter(project => project.featured);
  const filteredProjects = activeCategory === 'all' 
    ? featuredProjects 
    : featuredProjects.filter(project => project.category === activeCategory);

  // Highlight specific development contributions for each project
  const projectHighlights = {
    "1": {
      title: "Voice AI Platform & LLM Observability",
      role: "Senior Software Engineer",
      contributions: [
        "Built React Flow conversation editor with 50+ node types",
        "Implemented real-time transcript explorer with LLM traces", 
        "Created debugging tools that reduced agent iteration cycles by 70%"
      ],
      techFocus: ["React Flow", "WebSocket", "SSE", "LangChain", "TypeScript", "Python", "AWS", "OpenAI API", "Twilio Voice", "ngrok"]
    },
    "2": {
      title: "HIPAA-Compliant Healthcare Suite",
      role: "Full-Stack Developer", 
      contributions: [
        "Built Chrome extension with 99.9% HIPAA compliance",
        "Developed mobile app for offline note-taking",
        "Implemented AI-powered template extraction engine"
      ],
      techFocus: ["Medplum", "Chrome Extension", "FHIR", "React Native", "Expo", "Next.js", "Tailwind", "shadcn"]
    },
    "3": {
      title: "Construction Finance & AI CFO Platform",
      role: "Frontend Engineer",
      contributions: [
        "Built payment workflows processing $500M+ annually",
        "Created AI-powered CFO dashboard with risk analysis", 
        "Implemented real-time contractor analytics & reporting"
      ],
      techFocus: ["Angular 18", "Material UI", "AWS", "Terraform", "AI/ML"]
    },
    "4": {
      title: "Multi-Modal AML Investigation Platform", 
      role: "Senior Software Engineer",
      contributions: [
        "Built agent workflow studio with 70+ investigation modules",
        "Integrated phone verification, ID document analysis, and paystub validation",
        "Implemented compliance reporting that reduced false positives by 80%" 
      ],
      techFocus: ["ConductorOSS", "TypeScript", "Python", "GCP (GKE)", "Veriff", "AWS Rekognition", "AWS Textract", "Google Document AI", "Tesseract OCR"]
    },
    "5": {
      title: "AI CMO Marketing Platform",
      role: "Solo Developer",
      contributions: [
        "Built MVP from concept to working prototype in 8 weeks",
        "Created strategy automation dashboard with AI-driven insights",
        "Implemented campaign optimization engine with real-time A/B testing"
      ],
      techFocus: ["Next.js", "TypeScript", "Nest.js", "AWS Lambda", "Claude API", "OpenAI GPT-4", "Stripe API", "SendGrid"]
    },
    "6": {
      title: "AI Infrastructure & Observability Platform",
      role: "Backend Engineer", 
      contributions: [
        "Built cost monitoring dashboard tracking $2M+ monthly AI spend",
        "Created governance controls for 100+ AI models and pipelines",
        "Implemented compliance framework for enterprise AI deployment"
      ],
      techFocus: ["LiteLLM", "LangFlow", "KubeFlow", "AWS", "LangFuse", "React", "TypeScript", "Python", "Grafana", "Prometheus"]
    },
    "7": {
      title: "Property Management & Screening Platform",
      role: "Frontend Engineer",
      contributions: [
        "Built tenant screening interface processing 10K+ applications monthly",
        "Created automated background check workflow with TransUnion integration", 
        "Implemented real-time communication system for landlord-tenant interactions"
      ],
      techFocus: ["React", "TypeScript", "Shared UI Library", "Storybook", "Playwright", "Cucumber", "CircleCI", "Pulumi"]
    },
    "8": {
      title: "AI Dental Reception System",
      role: "Tech Lead",
      contributions: [
        "Built appointment scheduling interface with 24/7 voice AI capabilities",
        "Created SMS/web chat integration for multi-channel patient communication",
        "Implemented analytics dashboard showing 80% reduction in missed calls"
      ],
      techFocus: ["React", "ElevenLabs", "Vapi", "Twilio SMS", "Twilio Voice", "WebRTC", "Socket.io", "Stripe Connect"]
    },
    "9": {
      title: "AutoML & Document Processing Platform",
      role: "Senior Software Engineer",
      contributions: [
        "Built interactive model training dashboard with real-time progress tracking",
        "Created explainability interface with D3.js visualizations for feature importance",
        "Implemented SQL-to-model converter reducing time-to-insights by 60%"
      ],
      techFocus: ["AWS Textract", "Pinecone", "React", "D3.js", "WebSockets", "H2O Driverless AI", "Apache Spark", "MLflow"]
    },
    "10": {
      title: "Enterprise Document AI & RAG Platform",
      role: "Senior Software Engineer",
      contributions: [
        "Built document analysis interface with multimodal AI capabilities",
        "Created RAG-powered chat system with citation-based responses",
        "Implemented security controls for processing sensitive enterprise data"
      ],
      techFocus: ["AWS Textract", "Chroma", "React", "TypeScript", "AWS Bedrock", "Auth0", "Pinecone", "LangChain", "OpenAI Embeddings"]
    },
    "11": {
      title: "Data Lakehouse & Analytics Platform",
      role: "Software Engineer II",
      contributions: [
        "Built cluster monitoring dashboard processing petabytes of data",
        "Created job lifecycle management interface with real-time status updates",
        "Implemented Delta Lake operations UI for unified data/AI workflows"
      ],
      techFocus: ["React", "TypeScript", "Apache Spark", "Delta Lake", "WebSockets", "Apache Airflow", "MLflow", "Databricks SQL"]
    },
    "12": {
      title: "Google Cloud Platform Console",
      role: "Software Engineer",
      contributions: [
        "Built reusable Angular components adopted across 15+ GCP teams",
        "Created IAM management interface with fine-grained permission controls",
        "Implemented monitoring dashboard with real-time cloud metrics"
      ],
      techFocus: ["AngularJS", "Angular 2+", "Material Design", "Go", "Python", "gRPC", "Bazel", "Cloud Endpoints", "Stackdriver"]
    }
  };

  return (
    <section id="projects" className="py-24 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/20 to-slate-900/10" />
        <div className="absolute top-32 right-32 w-96 h-96 bg-slate-400/2 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Code2 className="h-8 w-8 text-slate-400" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">
              Development Work
            </h2>
          </div>
          <motion.div 
            className="w-16 h-0.5 bg-slate-500 mx-auto mb-6"
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : { width: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Systems I've architected, interfaces I've crafted, and problems I've solved through thoughtful engineering
          </p>
        </motion.div>

        {/* Category Navigation */}
        <motion.div 
          className="flex flex-wrap justify-center gap-2 mb-12"
          initial={{ y: 20, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map((category) => {
            const IconComponent = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActive 
                    ? 'bg-slate-700/50 text-slate-200 border border-slate-600/50' 
                    : 'bg-slate-800/30 text-slate-400 border border-slate-700/30 hover:bg-slate-700/40 hover:text-slate-300'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                {category.label}
              </button>
            );
          })}
        </motion.div>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {filteredProjects.map((project, index) => {
              const highlight = projectHighlights[project.id];
              return (
                <motion.div 
                  key={`${activeCategory}-${project.id}`}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-slate-900/40 border border-slate-800/40 rounded-2xl overflow-hidden hover:bg-slate-900/60 hover:border-slate-700/60 transition-all duration-300 relative">
                    {/* Company Logo(s) */}
                    {project.logo && (
                      <div className={`absolute bottom-4 right-4 flex items-center gap-2 ${project.consultingLogo ? 'bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-lg p-2' : ''} opacity-80 hover:opacity-100 transition-all duration-300 hover:scale-105`}>
                        {project.consultingLogo && (
                          <div className="w-10 h-10 bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-lg p-1.5">
                            <img 
                              src={project.consultingLogo} 
                              alt="thoughtbot logo"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                        <div className={`${project.consultingLogo ? 'w-10 h-10 bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-lg p-1.5' : 'w-12 h-12 bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 rounded-lg p-2'}`}>
                          <img 
                            src={project.logo} 
                            alt={`${project.company} logo`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Project Header */}
                    <div className="p-8 pb-6 relative z-10">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-slate-50 mb-2 group-hover:text-slate-100 transition-colors">
                            {highlight?.title || project.title}
                          </h3>
                          {highlight?.role && (
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <Zap className="h-3 w-3 text-slate-400" />
                              <span className="text-sm text-slate-300">{highlight.role}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {project.company && (
                            <span className="text-xs text-slate-400 bg-slate-800/40 px-3 py-1.5 rounded-md border border-slate-700/40 whitespace-nowrap">
                              {project.company}
                            </span>
                          )}
                          {project.industry && (
                            <span className="text-xs text-slate-500 bg-slate-800/30 px-3 py-1.5 rounded-md border border-slate-700/30 whitespace-nowrap">
                              {project.industry}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* What I Built */}
                      {highlight?.contributions && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-slate-400 mb-3 uppercase tracking-wide">What I Built</h4>
                          <div className="space-y-2">
                            {highlight.contributions.map((contribution, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-2 flex-shrink-0" />
                                <p className="text-slate-300 text-sm leading-relaxed">{contribution}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Key Technologies */}
                      {highlight?.techFocus && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-slate-400 mb-3 uppercase tracking-wide">Key Technologies</h4>
                          <div className="flex flex-wrap gap-2">
                            {highlight.techFocus.map((tech, idx) => (
                              <span 
                                key={idx}
                                className="px-2 py-1 bg-slate-800/50 text-slate-300 rounded-md text-xs border border-slate-700/50"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 mt-6">
                        {project.url && (
                          <button
                            onClick={() => window.open(project.url, '_blank')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/60 text-slate-300 hover:text-slate-200 rounded-xl border border-slate-700/50 hover:border-slate-600/60 transition-all duration-300 text-sm"
                          >
                            <ExternalLink className="h-4 w-4" />
                            View Live
                          </button>
                        )}
                        {project.additionalUrl && (
                          <button
                            onClick={() => window.open(project.additionalUrl, '_blank')}
                            className="inline-flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-slate-300 transition-all duration-300 text-sm"
                          >
                            <ArrowRight className="h-4 w-4" />
                            Related
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Projects;